import { ServiceStep, ServiceConfig } from "../types/unified-services";

export interface ServiceStepFlow {
  steps: ServiceStep[];
  getNextStep: (currentStep: ServiceStep) => ServiceStep | null;
  getPreviousStep: (currentStep: ServiceStep) => ServiceStep | null;
  isValidStep: (step: ServiceStep) => boolean;
  getDisplayStepIndex: (step: ServiceStep) => number;
}

export const getServiceStepFlow = (serviceConfig: ServiceConfig): ServiceStepFlow => {
  const { serviceId } = serviceConfig;

  switch (serviceId) {
    case "body-measurement": {
      const steps = [ServiceStep.GUIDE, ServiceStep.UPLOAD, ServiceStep.FACE_BLUR, ServiceStep.HEIGHT_INPUT, ServiceStep.PROCESSING, ServiceStep.RESULTS];

      return {
        steps,
        getNextStep: (currentStep: ServiceStep) => {
          const currentIndex = steps.indexOf(currentStep);
          return currentIndex >= 0 && currentIndex < steps.length - 1 ? steps[currentIndex + 1] : null;
        },
        getPreviousStep: (currentStep: ServiceStep) => {
          const currentIndex = steps.indexOf(currentStep);

          if (currentStep === ServiceStep.HEIGHT_INPUT) {
            return ServiceStep.UPLOAD;
          }

          return currentIndex > 0 ? steps[currentIndex - 1] : null;
        },
        isValidStep: (step: ServiceStep) => steps.includes(step),
        getDisplayStepIndex: (step: ServiceStep) => {
          return steps.indexOf(step);
        },
      };
    }

    case "auto-tagging":
    case "visual-search": {
      const steps = [ServiceStep.GUIDE, ServiceStep.UPLOAD, ServiceStep.PROCESSING, ServiceStep.RESULTS];

      return {
        steps,
        getNextStep: (currentStep: ServiceStep) => {
          const currentIndex = steps.indexOf(currentStep);
          return currentIndex >= 0 && currentIndex < steps.length - 1 ? steps[currentIndex + 1] : null;
        },
        getPreviousStep: (currentStep: ServiceStep) => {
          const currentIndex = steps.indexOf(currentStep);
          return currentIndex > 0 ? steps[currentIndex - 1] : null;
        },
        isValidStep: (step: ServiceStep) => steps.includes(step),
        getDisplayStepIndex: (step: ServiceStep) => {
          return steps.indexOf(step);
        },
      };
    }

    case "garment-measurement": {
      const steps = [ServiceStep.GUIDE, ServiceStep.UPLOAD, ServiceStep.PROCESSING, ServiceStep.RESULTS];

      return {
        steps,
        getNextStep: (currentStep: ServiceStep) => {
          const currentIndex = steps.indexOf(currentStep);
          return currentIndex >= 0 && currentIndex < steps.length - 1 ? steps[currentIndex + 1] : null;
        },
        getPreviousStep: (currentStep: ServiceStep) => {
          const currentIndex = steps.indexOf(currentStep);
          return currentIndex > 0 ? steps[currentIndex - 1] : null;
        },
        isValidStep: (step: ServiceStep) => steps.includes(step),
        getDisplayStepIndex: (step: ServiceStep) => {
          return steps.indexOf(step);
        },
      };
    }

    default: {
      const steps = [ServiceStep.GUIDE, ServiceStep.UPLOAD, ServiceStep.PROCESSING, ServiceStep.RESULTS];

      return {
        steps,
        getNextStep: (currentStep: ServiceStep) => {
          const currentIndex = steps.indexOf(currentStep);
          return currentIndex >= 0 && currentIndex < steps.length - 1 ? steps[currentIndex + 1] : null;
        },
        getPreviousStep: (currentStep: ServiceStep) => {
          const currentIndex = steps.indexOf(currentStep);
          return currentIndex > 0 ? steps[currentIndex - 1] : null;
        },
        isValidStep: (step: ServiceStep) => steps.includes(step),
        getDisplayStepIndex: (step: ServiceStep) => {
          return steps.indexOf(step);
        },
      };
    }
  }
};

export const validateAndCorrectStep = (step: ServiceStep, serviceConfig: ServiceConfig): ServiceStep => {
  const stepFlow = getServiceStepFlow(serviceConfig);

  if (stepFlow.isValidStep(step)) {
    return step;
  }

  if (step > ServiceStep.GUIDE) {
    for (let i = step; i >= ServiceStep.GUIDE; i--) {
      if (stepFlow.isValidStep(i as ServiceStep)) {
        return i as ServiceStep;
      }
    }
  }

  return ServiceStep.GUIDE;
};

export const getDisplaySteps = (serviceConfig: ServiceConfig): Array<{ step: ServiceStep; label: string; index: number }> => {
  const stepFlow = getServiceStepFlow(serviceConfig);
  const displaySteps = stepFlow.steps;

  return displaySteps.map((step, index) => {
    let label = "";
    switch (step) {
      case ServiceStep.GUIDE:
        label = "Guide";
        break;
      case ServiceStep.UPLOAD:
        label = "Upload";
        break;
      case ServiceStep.FACE_BLUR:
        label = "Privacy";
        break;
      case ServiceStep.HEIGHT_INPUT:
        label = "Height";
        break;
      case ServiceStep.PROCESSING:
        label = "Processing";
        break;
      case ServiceStep.RESULTS:
        label = getResultsLabel(serviceConfig.serviceId);
        break;
      default:
        label = "Step";
    }

    return { step, label, index };
  });
};

const getResultsLabel = (serviceId: string): string => {
  switch (serviceId) {
    case "body-measurement":
      return "Results";
    case "auto-tagging":
      return "Tags";
    case "visual-search":
      return "Search";
    case "garment-measurement":
      return "Results";
    default:
      return "Results";
  }
};
